import type { RecipeLineWithName } from '@/api/types'
import { cn } from '@/lib/cn'
import { formatGrams, formatNumber } from '@/lib/format'
import { pluralizeUnit } from '@/lib/units'
import { Check } from 'lucide-react'
import { useState } from 'react'

/** How the line reads ("2 cans"), falling back to grams. */
function measureOf(line: RecipeLineWithName): { main: string | null; grams: string | null } {
  const written = [
    line.display_amount === null
      ? null
      : formatNumber(line.display_amount, Number.isInteger(line.display_amount) ? 0 : 1),
    pluralizeUnit(line.display_unit, line.display_amount),
  ]
    .filter(Boolean)
    .join(' ')
  const grams = line.amount_in_grams === null ? null : formatGrams(line.amount_in_grams)
  if (written) return { main: written, grams }
  return { main: grams, grams: null }
}

/**
 * Ingredient list for reading and cooking. Rows can be ticked off while
 * cooking; the ticks are local and never saved. The component is keyed by
 * recipe at the call site, so a different recipe starts unticked.
 */
export function IngredientList({
  lines,
  servings,
  checkable = true,
}: {
  lines: RecipeLineWithName[]
  servings: number
  checkable?: boolean
}) {
  const [checked, setChecked] = useState<ReadonlySet<number>>(() => new Set())

  function toggle(id: number) {
    setChecked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <section>
      <div className="flex h-8 items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-ink">
          Ingredients{' '}
          <span className="font-normal text-ink-faint">
            · {lines.length} · {servings} servings
          </span>
        </h2>
        {checkable && checked.size > 0 ? (
          <button
            type="button"
            onClick={() => setChecked(new Set())}
            className="text-xs text-ink-faint hover:text-ink"
          >
            Clear ticks
          </button>
        ) : null}
      </div>

      {lines.length === 0 ? (
        <p className="mt-2 text-sm text-ink-faint">No ingredients recorded.</p>
      ) : (
        <ul className="mt-2">
          {lines.map((line) => {
            const { main, grams } = measureOf(line)
            const note = line.note?.trim()
            const isChecked = checked.has(line.link_id)
            const content = (
              <>
                <span className="tabular min-w-0 text-right text-ink">
                  <span className="font-medium">{main ?? ''}</span>
                  {grams ? <span className="block text-xs text-ink-faint">{grams}</span> : null}
                </span>
                <span className="min-w-0 text-pretty text-ink">
                  {line.ingredient_name}
                  {note ? <span className="text-ink-faint">, {note}</span> : null}
                </span>
              </>
            )
            const rowClass = cn(
              'grid w-full grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-x-3 rounded-md px-2 py-1.5 text-left text-sm',
              isChecked && 'opacity-45 [&>span:last-child]:line-through',
            )
            return (
              <li key={line.link_id} className="relative">
                {checkable ? (
                  <button
                    type="button"
                    aria-pressed={isChecked}
                    onClick={() => toggle(line.link_id)}
                    className={cn(rowClass, 'transition-colors hover:bg-surface-sunken')}
                  >
                    {content}
                  </button>
                ) : (
                  <div className={rowClass}>{content}</div>
                )}
                {isChecked ? (
                  <Check
                    size={14}
                    aria-hidden
                    className="pointer-events-none absolute top-2.5 left-0 text-accent"
                  />
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
